import {announce} from '@github-ui/aria-live'
import {Heading} from '@primer/react'
import {useNavigationType} from '@github-ui/react-router'
import {useEffect, useRef} from 'react'
import {getCountText} from '@github-ui/count'

import styles from './AIResourceSearchPage.module.css'

type SearchResultsHeaderProps = {
  elapsedMilliseconds?: number
  errorMessage?: string
  query: string
  status: 'success' | 'loading' | 'empty' | 'error'
  totalCount?: number
  visuallyHidden?: boolean
}

export function SearchResultsHeader({
  elapsedMilliseconds,
  errorMessage,
  query,
  status,
  totalCount,
  visuallyHidden,
}: SearchResultsHeaderProps) {
  const navigationAction = useNavigationType()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const previousStatusRef = useRef(status)
  let summary: string

  switch (status) {
    case 'success': {
      summary =
        totalCount === undefined
          ? `Results for ${query}`
          : `${getCountText(totalCount)} ${totalCount === 1 ? 'result' : 'results'} for ${query}`
      break
    }
    case 'loading':
      summary = `Loading AI resources for ${query}`
      break
    case 'empty':
    case 'error':
      summary = `0 results for ${query}`
      break
  }
  const announcement = errorMessage ?? summary

  useEffect(() => {
    const wasLoading = previousStatusRef.current === 'loading'
    previousStatusRef.current = status

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Focus follows completion of route-managed loading.
    if (wasLoading && status !== 'loading' && navigationAction !== 'POP') {
      headingRef.current?.focus()
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Error announcement follows route-managed completion.
      if (status === 'error') announce(announcement)
      return
    }

    announce(announcement)
  }, [announcement, navigationAction, status])

  return (
    <div className={visuallyHidden ? undefined : styles.ResultsHeader}>
      <Heading
        as="h2"
        className={visuallyHidden ? 'sr-only' : styles.ResultsHeading}
        data-react-autofocus={status === 'loading' ? undefined : true}
        id="search-results-count"
        ref={headingRef}
        tabIndex={-1}
      >
        {summary}
      </Heading>
      {elapsedMilliseconds !== undefined && status !== 'loading' ? (
        <span className={styles.ResultsTime}>({formatElapsedTime(elapsedMilliseconds)})</span>
      ) : null}
    </div>
  )
}

function formatElapsedTime(elapsedMilliseconds: number) {
  return elapsedMilliseconds >= 1000 ? `${Math.round(elapsedMilliseconds / 1000)} s` : `${elapsedMilliseconds} ms`
}
